import type { EpicDependencies } from "../../../rxjs/configureEpics"
import {
    createActionFetchPreparationTimeByItemReportFailure,
    createActionFetchPreparationTimeByItemReportSuccess,
    createActionFetchPreparationTimeHourlyReportFailure,
    createActionFetchPreparationTimeHourlyReportSuccess,
    FETCH_PREPARATION_TIME_BY_ITEM_REPORT_REQUESTED,
    FETCH_PREPARATION_TIME_HOURLY_REPORT_REQUESTED,
    fetchPreparationTimeByItemReportRequested,
    fetchPreparationTimeHourlyReportRequested,
} from "../../../../api/actions"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../../auth/actions"
import { areAnyLocationsSelected } from "../../../filters/selectors"
import { defer, merge, Observable } from "rxjs"
import { getHttpHeaders } from "../../../auth/selectors"
import {
    createLoadingEnded,
    createLoadingStarted,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_GLOBAL_DEFAULT_FILTER,
} from "../../../filters/actions"
import { getPreparationTimeReportQueryApiParam, isPreparationTimeReportAgendaOpened } from "./selectors"
import { fetchPreparationTimeByItemReport, fetchPreparationTimeHourlyReport } from "./request"
import {
    createActionClearPreparationTimeReportData,
    createActionPreparationTimeAgendaMounted,
    PREPARATION_TIME_AGENDA_MOUNTED,
} from "./actions"
import { GLOBAL_FILTER_IDENTIFIER } from "../../../filters/reducer"
import { waitForAccountSelected, waitForGlobalFilterSet } from "../customers/customersTable/epics"
import { Action, StateObject } from "../../../redux/types"
import { ofType, StateObservable } from "redux-observable"
import { catchError, delayWhen, filter, switchMap } from "rxjs/operators"

export const fetchPreparationTimeReportOnAccountSwitch = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED),
        filter(() => isPreparationTimeReportAgendaOpened(deps.getState())),
        switchMap(() => [createActionPreparationTimeAgendaMounted()])
    )

export const preparationTimeAgendaMountedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(PREPARATION_TIME_AGENDA_MOUNTED),
        switchMap(() => [
            createActionClearPreparationTimeReportData(),
            fetchPreparationTimeByItemReportRequested(),
            fetchPreparationTimeHourlyReportRequested(),
        ])
    )

export const preparationTimeReportFilterChanged = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    merge(
        actions$.pipe(ofType(SET_GLOBAL_DEFAULT_FILTER)),
        actions$.pipe(
            ofType(SET_FILTERS, SET_FILTER_VALUE),
            filter((action: Action) => action.payload.identifier === GLOBAL_FILTER_IDENTIFIER)
        )
    ).pipe(
        filter(() => isPreparationTimeReportAgendaOpened(deps.getState())),
        switchMap(() => {
            return [
                createActionClearPreparationTimeReportData(),
                fetchPreparationTimeByItemReportRequested(),
                fetchPreparationTimeHourlyReportRequested(),
            ]
        })
    )

export const fetchPreparationTimeByItemReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_PREPARATION_TIME_BY_ITEM_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isPreparationTimeReportAgendaOpened(deps.getState())),
        delayWhen((action: Action) => waitForAccountSelected(actions$, deps.getState(), action)),
        delayWhen((action: Action) => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => {
            if (!areAnyLocationsSelected(deps.getState())) {
                return [
                    createActionClearPreparationTimeReportData(),
                    createLoadingEnded("preparation-time-by-item-report"),
                ]
            }

            const headers = getHttpHeaders(deps.getState())
            const params = getPreparationTimeReportQueryApiParam(deps.getState())
            return defer(() => fetchPreparationTimeByItemReport(headers, params)).pipe(
                switchMap(result => {
                    if (!result) {
                        throw new Error("fetchPreparationTimeByItemReportRequestedEpics - no result")
                    }

                    return [
                        createLoadingEnded("preparation-time-by-item-report"),
                        createActionFetchPreparationTimeByItemReportSuccess(result),
                    ]
                }),
                catchError(error => {
                    return [
                        createLoadingEnded("preparation-time-by-item-report"),
                        createActionFetchPreparationTimeByItemReportFailure(error),
                        createActionClearPreparationTimeReportData(),
                    ]
                })
            )
        })
    )

export const fetchPreparationTimeHourlyReportRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_PREPARATION_TIME_HOURLY_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isPreparationTimeReportAgendaOpened(deps.getState())),
        delayWhen(action => waitForAccountSelected(actions$, deps.getState(), action)),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        switchMap(() => {
            if (!areAnyLocationsSelected(deps.getState())) {
                return [
                    createActionClearPreparationTimeReportData(),
                    createLoadingEnded("preparation-time-hourly-report"),
                ]
            }

            const headers = getHttpHeaders(deps.getState())
            const params = getPreparationTimeReportQueryApiParam(deps.getState())
            return defer(() => fetchPreparationTimeHourlyReport(headers, params)).pipe(
                switchMap(result => {
                    if (!result) {
                        throw new Error("fetchPreparationTimeHourlyReportRequestedEpics - no result")
                    }

                    return [
                        createLoadingEnded("preparation-time-hourly-report"),
                        createActionFetchPreparationTimeHourlyReportSuccess(result),
                    ]
                }),
                catchError(error => {
                    return [
                        createLoadingEnded("preparation-time-hourly-report"),
                        createActionFetchPreparationTimeHourlyReportFailure(error),
                        createActionClearPreparationTimeReportData(),
                    ]
                })
            )
        })
    )

export const preparationTimeByItemLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_PREPARATION_TIME_BY_ITEM_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isPreparationTimeReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("preparation-time-by-item-report")])
    )

export const preparationTimeHourlyLoadingStartedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FETCH_PREPARATION_TIME_HOURLY_REPORT_REQUESTED, REFETCH_DATA),
        filter(() => isPreparationTimeReportAgendaOpened(deps.getState())),
        switchMap(() => [createLoadingStarted("preparation-time-hourly-report")])
    )

export default [
    fetchPreparationTimeReportOnAccountSwitch,
    preparationTimeAgendaMountedEpics,
    preparationTimeReportFilterChanged,
    fetchPreparationTimeByItemReportRequestedEpics,
    fetchPreparationTimeHourlyReportRequestedEpics,
    preparationTimeByItemLoadingStartedEpics,
    preparationTimeHourlyLoadingStartedEpics,
]
